<template>
    <div cy-top-service-select class="full" :class="this.wrapperClass">
        <div class="row" :class="{'flex-row-reverse': !memberFirst, 'justify-content-end': !memberFirst}">
            <div class="col-auto px-md-2">
                <div class="field-label" v-if="showFieldLabel">{{provider.landing_member_label || $t('provider.member_user')}}</div>
                <ding-select
                    ref="memberSelect"
                    v-model="selectedMember"
                    :options="memberOptions"
                    :placeholder="provider.landing_member_label ? $t('message.select_custom', {custom: provider.landing_member_label}) : $t('message.select_member')"
                    value-attribute="id"
                    :preselectFirst="memberOptions.length === 1"
                    label-attribute="name">
                </ding-select>
            </div>
            <div class="col-auto px-md-2">
                <service-select
                    ref="serviceSelect"
                    :showFieldLabel="showFieldLabel"
                    v-model="selectedService"
                    :provider="provider"
                    :preselectFirst="false"
                    :options="serviceOptions"></service-select>
            </div>
        </div>
        <loading-indicator :is-loading="isLoading" :fullscreen="true"></loading-indicator>
    </div>
</template>

<script>
import moment from 'moment';
import 'moment-timezone';
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import loadingIndicator from '../common/LoadingIndicator';
import dingSelect from '../common/Select';
import availableDateTimePicker from './AvailableDateTimePicker';
import fullOverlay from '../common/FullOverlay';
import _ from 'lodash';
import serviceSelect from '../landing/TimeCheckingServiceSelect';
import { EventSelectMember, EventSelectService } from '../../bu/models/GA4';
import { mapGetters } from 'vuex';
import { getExchangedAmount } from '../../bu/CurrencyUtils';

export default {
    components: { fullOverlay, loadingIndicator, dingSelect, availableDateTimePicker, serviceSelect },
    mixins: [api, promptConfirm],
    props: {
        wrapperClass: String,
        provider: { type: Object, required: true },
        context: { type: Object, require: true},
        showFieldLabel: { type: Boolean, default: true },
        preselectedService: {type: Object, required: false},
        preselectedMember: {type: Object, required: false}
    },

    watch: {
        selectedService() {
            this.$emit('service-selected', this.selectedService);
            this.trackSelectService();
            this.goToTop();

            if (this.selectedService && this.selectedMember) {
                const currentMemberProviderCurrentService = this.memberForCurrentServices.filter((item) => item.id === this.selectedMember.id).length > 0;
                if (!currentMemberProviderCurrentService) {
                    this.selectedMember = null;
                }

                if (this.$refs.availableDateTimePicker) {
                    this.$refs.availableDateTimePicker.clear();
                }
            }

            this.preselectMember();
        },
        selectedMember() {
            this.$emit('member-selected', this.selectedMember);
            this.trackSelectMember();

            if (this.selectedService && this.selectedMember) {
                const currentMemberProviderCurrentService = this.memberForCurrentServices.filter((item) => item.id === this.selectedMember.id).length > 0;
                if (!currentMemberProviderCurrentService) {
                    this.selectedService = null;
                }
                if (this.$refs.availableDateTimePicker) {
                    this.$refs.availableDateTimePicker.clear();
                }
            }
        }
    },
    computed: {
        ...mapGetters({
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
        allServices() {
            return this.provider.services.filter((s) => {
                if (s.archived_at) {
                    return false;
                }

                if (s.is_disabled && !s.is_preview) {
                    return false;
                }

                if (s.is_private) {
                    return false;
                }

                let membersForService = this.provider.members.filter((member) => {
                    if (member.pivot.deleted_at || member.pivot.is_internal) {
                        return false;
                    }
                    return member.services.filter((memberService) => {
                        return memberService.id === s.id;
                    }).length > 0;
                });

                if (membersForService.length === 0) {
                    return false;
                }

                if (s.limited_periods && s.limited_periods.length > 0) {
                    var now = moment();
                    var validPeriods = s.limited_periods.filter((p) => {
                        return moment.tz(p.end, 'GMT').isAfter(now);
                    });
                    if (validPeriods.length === 0) {
                        return false;
                    }
                }

                return true;
            }).map((s) => {
                var title = s.title;
                var price = '';
                switch (s.price_policy) {
                    case 'min':
                        price = [this.getExchangedAmount(s.price_min), this.$t('services.price_up')].join(' ');
                        break;
                    case 'fixed':
                        price = [this.getExchangedAmount(s.price_min)].join(' ');
                        break;
                    case 'range':
                        price = [this.getExchangedAmount(s.price_min), this.$t('general.to'), s.price_max].join(' ');
                        break;
                    default:
                        break;
                }
                var ret = s;
                ret.value = s.id;
                ret.price = price;
                return ret;
            }).sort((a, b) => {
                return a.order - b.order;
            });
        },
        serviceOptions() {
            if (!this.memberFirst) { // service frist
                return this.allServices;
            } else {
                return this.serviceOptionsForCurrentMember;
            }
        },
        memberOptions() {
            var members = [];
            if (this.memberFirst) {
                members = this.aliveMembers;
            } else {
                members = this.memberForCurrentServices;
            }
            members.sort((m1, m2) => {
                return m1.pivot.order - m2.pivot.order;
            })
            return members;
        },
        aliveMembers() {
            return this.provider.members.filter((u) => {
                return !u.pivot.deleted_at && u.services.length > 0 && !u.pivot.is_internal;
            });
        },
        serviceOptionsForCurrentMember() {
            var serviceOptions = [];

            if (this.selectedMember) {
                let members = this.aliveMembers.filter((item) => { return item.id === this.selectedMember.id});
                if (members.length) {
                    var member = members[0];
                    let memberAvailableServiceIds = member.services.map((item) => { return item.id; });
                    serviceOptions = this.allServices.filter((service) => {
                        return memberAvailableServiceIds.indexOf(service.id) >= 0;
                    });
                }
            }

            return serviceOptions;
        },
        memberForCurrentServices() {
            let members = this.aliveMembers.filter((u) => {
                if (u.pivot.deleted_at || u.pivot.is_internal) {
                    return false;
                }
                for (var i in u.services) {
                    const service = u.services[i];
                    if (this.selectedService && service.id === this.selectedService.id) {
                        return true;
                    }
                }
                return false;
            });
            return members;
        },
    },
    data() {
        return {
            selectedService: this.preselectedService,
            selectedMember: this.preselectedMember,
            isLoading: false,
            memberFirst: this.provider.ask_order === 'ASK_ORDER_MEMBER_FIRST'
        }
    },
    mounted() {
        if (this.preselectedService && !this.preselectedMember) {
            this.preselectMember();
        }
    },
    methods: {
        goToTop(){
            document.scrollingElement.scrollTo({top: 0, behavior: 'smooth'});
        },
        trackSelectService() {
            window.tracker.track(() => new EventSelectService(this.selectedService));
        },
        trackSelectMember() {
            window.tracker.track(() => new EventSelectMember(this.selectedMember));
        },
        getExchangedAmount(amount) {
            return getExchangedAmount({
                rateMap: this.currencyRateMap,
                amount,
                from: this.provider.currency || 'TWD',
                to: this.currency,
                currencyOptions: this.currencyOptions,
            });
        },
        preselectMember() {
            if (this.selectedService && !this.selectedMember && this.memberForCurrentServices.length === 1) {
                this.selectedMember = this.memberForCurrentServices[0];
            }
        },
        openMemberSelect() {
            this.$refs.memberSelect?.open();
        },
        openServiceSelect() {
            this.$refs.serviceSelect?.open();
        }
    }
}
</script>

<style lang="scss" scoped>
    .time-checking-component {
        position: relative;
        width: 90%;
        margin: 0 auto;
    }

    @media (min-width: 768px) {
        .time-checking-component {
            width: 66%;
        }
    }
    @media (min-width: 800px) {
        .time-checking-component {
            width: 64%;
        }
    }

    .full /deep/ .field-label {
        font-size: 12px !important;
        font-weight: bold;
        color: #999;
        margin-bottom: 4px;
    }
</style>
